import React, { useState } from 'react'
import { formatValueFromIsKg } from 'src/utils'
import { useTheme } from 'styled-components'

import Button from '../../../../components/Button'
import Modal from '../../../../components/Modal'
import * as S from './styles'
import * as yup from 'yup'
import Form from 'src/components/FormComponents/Form'
import FieldV2 from 'src/components/FormComponents/FieldV2'

import { confirmWeightDivergence } from 'src/services/receivement/receivementService'
import { toast } from 'react-toastify'
import { useForm } from 'react-hook-form'
import { yupResolver } from '@hookform/resolvers/yup'
import '../../../../styles/modal.css'

const schema = yup.object().shape({
  badge: yup.string().trim().required('O crachá do supervisor é obrigatório!'),
})

interface Props {
  showModal: boolean;
  setShowModal: (showModal: boolean) => void;
  finishFraction: (handleLoading: (showLoading: boolean) => void) => void;
  totalQuantity: number;
  totalFractions: number;
  totalFractioned: number;
  measureUnit: string;
  isKg: boolean;
}

const ModalAlertTotalFractioned = ({
  showModal,
  setShowModal,
  finishFraction,
  totalQuantity,
  totalFractions,
  totalFractioned,
  measureUnit,
  isKg,
}: Props) => {
  const title = 'Lote inconsistente'
  const theme = useTheme()
  const totalFractionedFromIsKg = isKg ? totalFractioned : totalFractions
  const totalQuantityFromIsKg = isKg ? totalQuantity * 1000 : Math.trunc(totalQuantity)

  const [loading, setLoading] = useState(false)
  const { handleSubmit, control } = useForm({ resolver: yupResolver(schema) })

  const handleClose = () => {
    setShowModal(false)
  }
  const handleFinish = React.useCallback(() => {
    finishFraction(setLoading)
  }, [finishFraction])

  const onSubmit = React.useCallback(
    async (data) => {
      const result = await confirmWeightDivergence(data.badge)
      if (result.isOk) {
        handleFinish()
      } else {
        toast.error(result?.data?.message || 'Falha ao validar o crachá do supervisor!')
      }
    },
    [handleFinish],
  )

  const diff = React.useMemo(
    () => totalQuantityFromIsKg - totalFractionedFromIsKg,
    [totalQuantityFromIsKg, totalFractionedFromIsKg],
  )

  const diffAbs = React.useMemo(() => Math.abs(diff), [diff])

  const fractionationText = React.useMemo(
    () => (
      <S.Text>
        O Fracionamento possui{' '}
        <b>
          {formatValueFromIsKg(diffAbs, isKg)} {measureUnit} a {diff > 0 ? 'menos' : 'mais'}
        </b>{' '}
        do que o lote inicial.
      </S.Text>
    ),
    [diffAbs, measureUnit, diff, isKg],
  )

  const diffPercentage = React.useMemo(() => {
    if (totalQuantityFromIsKg === 0) {
      return '0%'
    }
    return `${((diffAbs / totalQuantityFromIsKg) * 100).toFixed(2)}%`
  }, [diffAbs, totalQuantityFromIsKg])

  return (
    <Modal
      show={showModal}
      closedButton={false}
      title={title}
      centered
      dialogClassName='modal-dialog-600'
      styleHeader={{ backgroundColor: theme.colors.orange }}
      titleStyle={{ fontFamily: 'Work Sans', fontSize: '20px', fontWeight: 'bold' }}
      IconLeft={() => <S.Icon />}
      body={(
        <S.BodyModal>
          <S.InfoContainer>
            <S.InfoRow>
              <S.LabelColumn>
                <S.Label>Quantidade Total do Lote:</S.Label>
              </S.LabelColumn>
              <S.ValueColumn>
                <S.Value>
                  {formatValueFromIsKg(totalQuantityFromIsKg, isKg)} {measureUnit}
                </S.Value>
              </S.ValueColumn>
            </S.InfoRow>
            <S.InfoRow>
              <S.LabelColumn>
                <S.Label>Fracionado + Perda: </S.Label>
              </S.LabelColumn>
              <S.ValueColumn>
                <S.Value>
                  {formatValueFromIsKg(totalFractionedFromIsKg, isKg)} {measureUnit}
                </S.Value>
              </S.ValueColumn>
            </S.InfoRow>
            <S.InfoRow>
              <S.LabelColumn>
                <S.Label>Diferença: </S.Label>
              </S.LabelColumn>
              <S.ValueColumn>
                <S.Value data-testid='alert-total-fractioned-diff'>
                  ({diffPercentage}) {formatValueFromIsKg(diffAbs, isKg)} {measureUnit}
                </S.Value>
              </S.ValueColumn>
            </S.InfoRow>
          </S.InfoContainer>

          <S.Form as={Form} onSubmit={handleSubmit(onSubmit)}>
            <S.InputContainer>
              <S.InputLabel htmlFor='badge'>Crachá do supervisor</S.InputLabel>
              <S.Field
                as={FieldV2}
                type='password'
                name='badge'
                autoFocus
                autoComplete='off'
                control={control}
                showErrorContainer
                testId='badge-input'
              />
            </S.InputContainer>
            <S.TextContainer>
              {fractionationText}
              <S.Text>Antes de prosseguir verifique se não há nenhum lote perdido.</S.Text>
            </S.TextContainer>

            <S.ContainerButton>
              <Button
                style={S.ButtonStyle}
                type='submit'
                testId='submit-button'
                plainText='Prosseguir'
                color={theme.colors.blue}
                isDisabled={loading}
              />
              <Button
                style={S.ButtonStyle}
                plainText='Cancelar'
                onClick={handleClose}
                color={theme.colors.cancel}
              />
            </S.ContainerButton>
          </S.Form>
        </S.BodyModal>
      )}
    />
  )
}

export { ModalAlertTotalFractioned }
